/**
 * LiquidGauge.jsx
 * Gauge circular con animación de líquido ondulante (SVG puro, sin librerías)
 *
 * Props:
 *   pct    {number}  0-100
 *   color  {string}  hex color del líquido
 *   size   {number}  tamaño en px (default 90)
 *   label  {string}  texto secundario debajo del porcentaje (opcional)
 */

import { useMemo } from "react";

export default function LiquidGauge({ pct = 0, color = "#22C55E", size = 90, label = "" }) {
    const cx = size / 2;
    const cy = size / 2;
    const r = size * 0.44;
    const border = size * 0.045;

    // Nivel del líquido: 0% = abajo del todo, 100% = arriba del todo
    // Mapeamos pct a la coordenada Y dentro del círculo
    const liquidY = cy + r - (pct / 100) * (r * 2);

    // Color con opacidad para la ola secundaria
    const colorDim = color + "88";

    // Color del borde exterior según nivel
    const ringColor = pct <= 15 ? "#EF4444" : pct <= 25 ? "#F59E0B" : color;

    // ID único para el clipPath (evitar colisiones si hay múltiples gauges)
    const clipId = useMemo(() => `clip-${Math.random().toString(36).slice(2)}`, []);
    const waveId = useMemo(() => `wave-${Math.random().toString(36).slice(2)}`, []);

    const fontSize = size * 0.22;
    const fontSizeSub = size * 0.13;
    const textColor = pct <= 15 ? "#EF4444" : pct <= 25 ? "#F59E0B" : "#F9FAFB";

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ display: "block", overflow: "visible" }}
        >
            <defs>
                {/* Clip circular para el líquido */}
                <clipPath id={clipId}>
                    <circle cx={cx} cy={cy} r={r - border * 0.5} />
                </clipPath>

                {/* Animación de la ola */}
                <style>{`
          @keyframes ${waveId}_move {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes ${waveId}_rise {
            0%   { transform: translateY(0px); }
            50%  { transform: translateY(-${size * 0.015}px); }
            100% { transform: translateY(0px); }
          }
          .${waveId}-group {
            animation: ${waveId}_rise ${3 + pct * 0.02}s ease-in-out infinite;
          }
          .${waveId}-path {
            animation: ${waveId}_move ${2.5 - pct * 0.01}s linear infinite;
          }
        `}</style>
            </defs>

            {/* Fondo del círculo */}
            <circle cx={cx} cy={cy} r={r} fill="#0F172A" />

            {/* Líquido con ola — todo clipeado al círculo */}
            <g clipPath={`url(#${clipId})`}>
                {/* Fondo del líquido */}
                <rect
                    x={cx - r}
                    y={liquidY}
                    width={r * 2}
                    height={r * 2}
                    fill={color}
                    opacity={0.25}
                    style={{ transition: "y 1s ease" }}
                />

                {/* Ola animada */}
                <g
                    className={`${waveId}-group`}
                    style={{ transform: `translateY(${liquidY - cy}px)` }}
                >
                    {/* Ola principal */}
                    <path
                        className={`${waveId}-path`}
                        fill={color}
                        opacity={0.7}
                        d={`
              M ${cx - r * 3} ${cy}
              Q ${cx - r * 2.5} ${cy - size * 0.04}  ${cx - r * 2} ${cy}
              Q ${cx - r * 1.5} ${cy + size * 0.04}  ${cx - r}     ${cy}
              Q ${cx - r * 0.5} ${cy - size * 0.04}  ${cx}         ${cy}
              Q ${cx + r * 0.5} ${cy + size * 0.04}  ${cx + r}     ${cy}
              Q ${cx + r * 1.5} ${cy - size * 0.04}  ${cx + r * 2} ${cy}
              Q ${cx + r * 2.5} ${cy + size * 0.04}  ${cx + r * 3} ${cy}
              V ${cy + r * 3}
              H ${cx - r * 3}
              Z
            `}
                    />
                    {/* Ola secundaria (desfasada) */}
                    <path
                        className={`${waveId}-path`}
                        fill={colorDim}
                        opacity={0.5}
                        style={{ animationDelay: "-1.2s", animationDuration: `${2.0 - pct * 0.008}s` }}
                        d={`
              M ${cx - r * 3} ${cy}
              Q ${cx - r * 2.5} ${cy + size * 0.035} ${cx - r * 2} ${cy}
              Q ${cx - r * 1.5} ${cy - size * 0.035} ${cx - r}     ${cy}
              Q ${cx - r * 0.5} ${cy + size * 0.035} ${cx}         ${cy}
              Q ${cx + r * 0.5} ${cy - size * 0.035} ${cx + r}     ${cy}
              Q ${cx + r * 1.5} ${cy + size * 0.035} ${cx + r * 2} ${cy}
              Q ${cx + r * 2.5} ${cy - size * 0.035} ${cx + r * 3} ${cy}
              V ${cy + r * 3}
              H ${cx - r * 3}
              Z
            `}
                    />
                </g>

                {/* Brillo superior */}
                <ellipse
                    cx={cx - r * 0.2}
                    cy={cy - r * 0.55}
                    rx={r * 0.35}
                    ry={r * 0.12}
                    fill="white"
                    opacity={0.06}
                />
            </g>

            {/* Borde exterior */}
            <circle
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={ringColor}
                strokeWidth={border}
                opacity={0.9}
                style={{ transition: "stroke 0.5s ease" }}
            />

            {/* Borde interior sutil */}
            <circle
                cx={cx} cy={cy} r={r - border * 1.2}
                fill="none"
                stroke="rgba(255,255,255,0.04)"
                strokeWidth={1}
            />

            {/* Texto porcentaje */}
            <text
                x={cx}
                y={label ? cy - 1 : cy + fontSize * 0.35}
                textAnchor="middle"
                fontSize={fontSize}
                fontWeight="700"
                fill={textColor}
                fontFamily="'Roboto Mono', monospace"
                style={{ transition: "fill 0.5s ease" }}
            >
                {pct}%
            </text>

            {/* Subtexto opcional */}
            {label && (
                <text
                    x={cx}
                    y={cy + fontSizeSub * 1.4}
                    textAnchor="middle"
                    fontSize={fontSizeSub}
                    fill="rgba(255,255,255,0.4)"
                    fontFamily="'Roboto Mono', monospace"
                >
                    {label}
                </text>
            )}
        </svg>
    );
}